// @@CN_ACCOUNT_v108@@ Client profile › Zoho Books › Credit note account.
// The Zoho account this client's credit notes post to — store-report royalty credit
// notes AND POS daily credit notes use the same one (server: lib/cn-account.js).
// Empty = the default account.
// @@CN_DEFAULT_v109@@ The default is now editable: Settings › Zoho credit notes
// (DefaultCnAccountPanel below). Order: client's account -> Settings default ->
// ZOHO_ROYALTY_ACCOUNT_ID -> the Zoho account named "Royalty".
// Self-contained so its hooks never sit after ClientDetail's early returns.
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

const TYPE_LABEL = (t) => String(t || 'other').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

// The default's display name, from /api/settings (readable by every internal role).
function useDefaultName() {
  const [name, setName] = useState(null);
  useEffect(() => {
    let on = true;
    apiFetch('/api/settings').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (on) setName((d && d.settings && d.settings.default_cn_account_name) || 'Royalty');
    }).catch(() => on && setName('Royalty'));
    return () => { on = false; };
  }, []);
  return name;
}

// Generic picker. Either pass clientId (saves to that client) or onSave(accountId)
// returning a promise of { error } | { ok }.
export function AccountPicker({ label, hint, accountId, accountName, emptyLabel, canManage, onSave, bare }) {
  const [accounts, setAccounts] = useState(null); // null = not loaded
  const [pick, setPick] = useState(accountId || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => { setPick(accountId || ''); }, [accountId]);

  async function load(fresh) {
    setErr('');
    const r = await apiFetch('/api/clients/zoho-accounts' + (fresh ? '?fresh=1' : ''));
    const d = await r.json().catch(() => ({}));
    if (r.ok) setAccounts(d.accounts || []); else { setAccounts([]); setErr(d.error || 'Could not load Zoho accounts.'); }
  }
  useEffect(() => { if (open && accounts === null) load(false); }, [open]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const a of accounts || []) {
      const k = TYPE_LABEL(a.account_type);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return [...m.entries()];
  }, [accounts]);

  async function save() {
    setBusy(true); setErr('');
    const out = await onSave(pick || null);
    setBusy(false);
    if (out && out.error) { setErr(out.error); return; }
    setOpen(false);
  }

  const current = accountId ? (accountName || accountId) : emptyLabel;
  const wrap = bare ? { display: 'grid', gap: 8 } : { marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hq-line)', display: 'grid', gap: 8 };
  return (
    <div className="hq-cd-cnacct" style={wrap}>
      {label && <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--hq-muted)', fontWeight: 600 }}>{label}</div>}
      <div style={{ fontSize: 14 }}>
        <strong>{current || '…'}</strong>
        {hint && <div style={{ color: 'var(--hq-muted)', fontSize: 12 }}>{hint}</div>}
      </div>
      {canManage && !open && (
        <div><button type="button" className="hq-btn hq-btn-ghost hq-cd-sm" onClick={() => setOpen(true)}>Change</button></div>
      )}
      {canManage && open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="hq-select" value={pick} onChange={(e) => setPick(e.target.value)} disabled={accounts === null} style={{ flex: '1 1 240px', minWidth: 0 }} aria-label={label || 'Credit note account'}>
            <option value="">{accounts === null ? 'Loading Zoho accounts…' : emptyLabel}</option>
            {groups.map(([type, list]) => (
              <optgroup key={type} label={type}>
                {list.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}{a.account_code ? ` (${a.account_code})` : ''}</option>)}
              </optgroup>
            ))}
            {accountId && accounts && !accounts.some((a) => a.account_id === accountId) && <option value={accountId}>{accountName || accountId} (not found in Zoho)</option>}
          </select>
          <button type="button" className="hq-btn" disabled={busy || accounts === null || pick === (accountId || '')} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          <button type="button" className="hq-btn hq-btn-ghost" onClick={() => load(true)} disabled={busy}>Reload list</button>
          <button type="button" className="hq-btn hq-btn-ghost" onClick={() => { setOpen(false); setPick(accountId || ''); setErr(''); }}>Cancel</button>
        </div>
      )}
      {err && <div style={{ color: '#ff7a73', fontSize: 13 }}>{err}</div>}
    </div>
  );
}

// Client profile › Zoho Books card.
export default function CnAccountPicker({ clientId, accountId, accountName, canManage, onSaved }) {
  const defaultName = useDefaultName();
  async function onSave(id) {
    const r = await apiFetch(`/api/clients/${clientId}/cn-account`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account_id: id }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { error: d.error || 'Could not save.' };
    onSaved && onSaved(d.client);
    return { ok: true };
  }
  return (
    <AccountPicker label="Credit note account" accountId={accountId} accountName={accountName} canManage={canManage} onSave={onSave}
      emptyLabel={defaultName ? `Default — ${defaultName}` : 'Default'}
      hint={<>Used for this client’s store-report and POS credit notes. The default is set in Settings › Zoho credit notes.</>} />
  );
}

// @@CN_DEFAULT_v109@@ Settings › Zoho credit notes — the default account for every
// client without its own. Admin/manager (the Settings page is already gated).
export function DefaultCnAccountPanel() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    apiFetch('/api/settings').then((r) => (r.ok ? r.json() : null)).then((d) => setS((d && d.settings) || {})).catch(() => setS({}));
  }, []);
  async function onSave(id) {
    setMsg('');
    const r = await apiFetch('/api/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ default_cn_account_id: id }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { error: d.error || 'Could not save.' };
    setS(d.settings); setMsg('Saved. New credit notes use this account unless the client has its own.');
    return { ok: true };
  }
  return (
    <div className="hq-panel" style={{ marginTop: 20 }}>
      <div className="hq-panel-head"><h2>Zoho credit notes</h2></div>
      <div style={{ padding: 20, display: 'grid', gap: 10 }}>
        <p style={{ color: 'var(--hq-muted)', fontSize: 13, margin: 0 }}>
          The account store-report (royalty) and POS credit notes post to. A client can override it on its profile
          (Clients › client › Zoho Books › Credit note account).
        </p>
        {s === null ? <div style={{ color: 'var(--hq-muted)' }}>Loading…</div> : (
          <AccountPicker bare label="Default credit note account" canManage onSave={onSave}
            accountId={s.default_cn_account_id || null} accountName={s.default_cn_account_name || null}
            emptyLabel="Not set — the Zoho account named “Royalty”"
            hint="Not set falls back to ZOHO_ROYALTY_ACCOUNT_ID (if set on Render), then the account named “Royalty”." />
        )}
        {msg && <p className="hq-msg" style={{ margin: 0 }}>{msg}</p>}
      </div>
    </div>
  );
}
